import { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router";
import { createApiResponse } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import { distinct } from "@entrilia/shared/utilities/arrayHelper";
import adminApi from "../../../../api/adminApi";
import type { LookupFieldConfiguration, ObjectFieldValues } from "../../../../api/types/objectTypes";
import { useObjectDefinitionContext } from "../ObjectDefinitionContext";
import ChecklistEditView from "./ChecklistEditView";
import ChecklistsView from "./ChecklistsView";
import { getInitialChecklistConfigurationState } from "./checklistConfigurationState";

const defaultBackButtonPath = "..";

const getBackButtonPath = (locationState: unknown): string => {
  if (
    locationState &&
    typeof locationState === "object" &&
    "backButtonPath" in locationState &&
    typeof locationState.backButtonPath === "string"
  ) {
    return locationState.backButtonPath;
  }

  return defaultBackButtonPath;
};

const ChecklistsConfigurationPage = () => {
  const { objectDefinition } = useObjectDefinitionContext();
  const { state: locationState } = useLocation();
  const backButtonPath = getBackButtonPath(locationState);

  usePageTitle(`${objectDefinition.objectName} | Checklists`);

  const [state, setState] = useState(getInitialChecklistConfigurationState);

  const relatedObjectTypes = useMemo(() => {
    const objectTypesFromLookupFields = objectDefinition.fields
      .filter((f) => f.type === "Lookup" || f.type === "MultiLookup")
      .map((f) => (f.configuration as LookupFieldConfiguration | undefined)?.objectType)
      .filter(Boolean) as string[];

    return distinct(objectTypesFromLookupFields);
  }, [objectDefinition.fields]);

  const getRelatedObjectDefinitions = useCallback(
    () =>
      relatedObjectTypes.length === 0
        ? Promise.resolve(createApiResponse({}))
        : adminApi.getObjectDefinitionsByObjectTypes(relatedObjectTypes),
    [relatedObjectTypes]
  );

  const getTaskTemplates = useCallback(() => adminApi.searchObjects("ChecklistTaskTemplate", {}), []);

  const [relatedObjectDefinitions, fetchRelatedObjectDefinitionsError] = useFetch(getRelatedObjectDefinitions);
  const [taskTemplatesResp, fetchTaskTemplatesError, { setData: setTaskTemplates }] = useFetch(getTaskTemplates);

  const handleTaskTemplateCreated = useCallback(
    (newTaskTemplate: ObjectFieldValues) => {
      setTaskTemplates((prev) => (prev ? { ...prev, items: [...prev.items, newTaskTemplate] } : prev));
    },
    [setTaskTemplates]
  );

  const fetchError = fetchRelatedObjectDefinitionsError || fetchTaskTemplatesError;

  if (fetchError) {
    logError(fetchError, "[ChecklistsConfigurationPage] getRelatedObjectDefinitions");
    return <DataLoadingFailed title="Loading related object definitions failed" />;
  }

  if (relatedObjectDefinitions === undefined || taskTemplatesResp === undefined) {
    return <InlineLoader />;
  }

  if (state.view === "list") {
    return (
      <ChecklistsView
        relatedObjectDefinitions={relatedObjectDefinitions}
        taskTemplates={taskTemplatesResp.items}
        setState={setState}
        backButtonPath={backButtonPath}
      />
    );
  }

  if (state.view === "edit" || state.view === "add") {
    return (
      <ChecklistEditView
        state={state}
        setState={setState}
        taskTemplates={taskTemplatesResp.items}
        relatedObjectDefinitions={relatedObjectDefinitions}
        onTaskTemplateCreated={handleTaskTemplateCreated}
      />
    );
  }

  return null;
};

export default ChecklistsConfigurationPage;
